import React, { useMemo, useState } from 'react';
import { Btn, Loading, Msg, Section, fmtDate, panelGet, useAdminGet } from '../kit';
import { Pill, num, rowsOf, str } from '../ipintel/shared';
import { AdminLink } from '../adminNav';
import {
  ConfidencePill, Drawer, Fact, type HzCtx, HorizonPill, LeverChips, S, StatusPill, TextBlock, appsOf, fmtCost,
  leversOf, normCurrent, promptLabel, publicUrl, shortId,
} from './shared';

/**
 * One technology's Horizon story — GET horizon/tech {id}: the technology,
 * its current data (what the public page shows, and which prompt / run it
 * came from), and every result it ever got across prompts and runs, newest
 * first. A result expands in place; its raw model response is fetched on
 * demand (GET horizon/result — raw_response is left out of every list).
 */

const ResultCard: React.FC<{ r: any; ctx: HzCtx; isCurrent: boolean }> = ({ r, ctx, isCurrent }) => {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState<{ loading: boolean; text: string; error: string } | null>(null);
  const p = ctx.promptById(r.prompt_id);
  const levers = leversOf(r.value_lever_tags ?? r.levers);
  const apps = appsOf(r.example_applications ?? r.applications);
  const loadRaw = async () => {
    setRaw({ loading: true, text: '', error: '' });
    const { data, error } = await panelGet(S, 'result', { id: str(r.id) });
    const row = data?.result ?? data?.row ?? data;
    setRaw({ loading: false, text: str(row?.raw_response), error });
  };
  return (
    <div className={`rounded-lg border bg-white min-w-0 ${isCurrent ? 'border-green-300 ring-1 ring-green-200' : 'border-grayscale-300'}`}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="w-full text-left px-3 py-2 flex items-center gap-2 flex-wrap hover:bg-grayscale-100 rounded-lg">
        <span aria-hidden className="text-grayscale-1100 text-xs w-3">{open ? '▾' : '▸'}</span>
        <span className="text-xs text-grayscale-1200 whitespace-nowrap">{fmtDate(r.created_at)}</span>
        <HorizonPill h={r.horizon} />
        <ConfidencePill c={r.horizon_confidence} />
        {isCurrent && <Pill tone="good" title="The result the public page shows">current</Pill>}
        {p?.is_final && <Pill tone="info">final prompt</Pill>}
        {str(r.error) && <Pill tone="bad">error</Pill>}
        {r.model && <span className="text-[11px] text-grayscale-1100 whitespace-nowrap">{str(r.model)}</span>}
        <span className="text-xs text-grayscale-1100 min-w-0 truncate">
          {promptLabel(p, r.prompt_id)} · run <span className="font-mono">{shortId(r.run_id)}</span>
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 flex flex-col gap-3 min-w-0">
          {str(r.error) && <Msg kind="error">{str(r.error)}</Msg>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Fact label="Industry">{str(r.industry) || '—'}</Fact>
            <Fact label="Problem type">{str(r.primary_problem_type) || '—'}</Fact>
            <Fact label="Secondary types">{str(r.secondary_problem_types) || '—'}</Fact>
            <Fact label="Tokens · cost · time">
              {num(r.tokens).toLocaleString()} · {fmtCost(r.cost, true)} · {r.latency_ms ? `${(num(r.latency_ms) / 1000).toFixed(1)}s` : '—'}
            </Fact>
          </div>
          {str(r.inferred_problem) && (
            <div className="flex flex-col gap-1">
              <div className="text-xs font-bold text-grayscale-1200">Problem</div>
              <p className="text-sm text-grayscale-1300 break-words">{str(r.inferred_problem)}</p>
            </div>
          )}
          {apps.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-xs font-bold text-grayscale-1200">Example applications</div>
              <ul className="list-disc pl-5 text-sm text-grayscale-1300 flex flex-col gap-0.5">
                {apps.map((a, i) => <li key={i} className="break-words">{a}</li>)}
              </ul>
            </div>
          )}
          {levers.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-xs font-bold text-grayscale-1200">Value levers → technical tags</div>
              <LeverChips levers={levers} />
            </div>
          )}
          {str(r.reasoning) && (
            <details className="text-sm">
              <summary className="cursor-pointer text-xs font-bold text-grayscale-1200">Reasoning</summary>
              <p className="mt-1 text-grayscale-1300 whitespace-pre-wrap break-words">{str(r.reasoning)}</p>
            </details>
          )}
          {(r.reviewed || r.approved !== null && r.approved !== undefined || str(r.review_notes)) && (
            <div className="text-xs text-grayscale-1200">
              Review: {r.reviewed ? 'reviewed' : 'not reviewed'}
              {r.approved !== null && r.approved !== undefined ? ` · ${r.approved ? 'approved' : 'not approved'}` : ''}
              {str(r.corrected_horizon) ? ` · corrected horizon ${str(r.corrected_horizon)}` : ''}
              {str(r.review_notes) ? ` · ${str(r.review_notes)}` : ''}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Btn small onClick={loadRaw} disabled={raw?.loading}>{raw?.loading ? 'Loading…' : raw ? 'Reload raw response' : 'View raw response'}</Btn>
            <Btn small onClick={() => ctx.openResult(str(r.id))}>Open full result →</Btn>
            <Btn small onClick={() => ctx.goResults({ run: str(r.run_id) })}>All results of this run</Btn>
            <span className="text-[11px] font-mono text-grayscale-1100 break-all">{str(r.id)}</span>
          </div>
          {raw && !raw.loading && (raw.error ? <Msg kind="error">{raw.error}</Msg>
            : <TextBlock text={raw.text} mono maxHeight="26rem" empty="No raw response stored for this result." />)}
        </div>
      )}
    </div>
  );
};

const TechDrawer: React.FC<{ id: string; ctx: HzCtx; onClose: () => void }> = ({ id, ctx, onClose }) => {
  const res = useAdminGet(S, 'tech', { id }, [id, ctx.dataTick]);
  const d = res.data || {};
  const tech = d.tech || {};
  const cur = normCurrent(d.current);
  const results = useMemo(() => rowsOf(d.results), [d.results]);
  const curPrompt = cur ? ctx.promptById(cur.prompt_id) : undefined;
  const title = str(tech.title) || id;
  const path = str(tech.tech_page_path);
  // tech.university is {code, name, label, country}; older payloads sent the name.
  const uni = tech.university && typeof tech.university === 'object'
    ? str(tech.university.label || tech.university.name || tech.university.code) : str(tech.university);
  const uniLabel = str(tech.university_label) || uni;
  const usable = results.filter((r: any) => !str(r.error) && str(r.horizon)).length;

  return (
    <Drawer onClose={onClose} label={`Horizon data: ${title}`}
      header={
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Pill tone="info">Technology</Pill>
            {res.data && <StatusPill status={cur ? 'has' : 'pending'} horizon={cur?.horizon} />}
            <span className="text-[11px] font-mono text-grayscale-1100 break-all">{id}</span>
          </div>
          <div className="text-base font-bold text-grayscale-1300 font-grotesk break-words">{title}</div>
          {tech.in_corpus === false && (
            <div className="text-xs text-amber-700">No longer in the corpus — only its old Horizon results remain.</div>
          )}
          <div className="text-xs text-grayscale-1100 break-words">
            {[uniLabel, str(tech.industry), tech.trl !== undefined && tech.trl !== null && tech.trl !== '' ? `TRL ${tech.trl}` : '',
              tech.updated_date ? `updated ${fmtDate(tech.updated_date)}` : ''].filter(Boolean).join(' · ')}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-xs">
            {path ? (
              <a href={publicUrl(path)} target="_blank" rel="noopener noreferrer" title={publicUrl(path)}
                className="text-primary-700 underline underline-offset-2 whitespace-nowrap">Open public page ↗</a>
            ) : <span className="text-grayscale-1100">No public page URL yet</span>}
            <AdminLink to={{ group: 'tech_pages', tab: 'tech', target: id }}
              title="The page's traffic, sections, URL history and previews">Open in Tech URL pages</AdminLink>
          </div>
        </div>
      }>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading label="Reading this technology's Horizon results…" /> : !res.data ? null : (
        <>
          <Section title="Current Horizon data — what the public page shows">
            {cur ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Fact label="Horizon"><HorizonPill h={cur.horizon} long /></Fact>
                  <Fact label="Confidence"><ConfidencePill c={cur.confidence} /></Fact>
                  <Fact label="Industry">{cur.industry || '—'}</Fact>
                  <Fact label="Result from" title={cur.created_at}>{cur.created_at ? fmtDate(cur.created_at) : '—'}</Fact>
                </div>
                <div className="text-xs text-grayscale-1200 flex items-center gap-2 flex-wrap">
                  <span>Prompt:</span>
                  <button type="button" onClick={() => ctx.goPrompt(cur.prompt_id)} className="underline underline-offset-2 text-left">
                    {promptLabel(curPrompt, cur.prompt_id)}
                  </button>
                  {curPrompt?.is_final ? <Pill tone="good">FINAL</Pill>
                    : <Pill tone="warn" title="The final prompt has no usable result for this technology; this came from a fallback prompt">fallback</Pill>}
                  <span>· run</span>
                  <button type="button" onClick={() => ctx.goResults({ run: cur.run_id })} className="font-mono underline underline-offset-2">
                    {shortId(cur.run_id) || '—'}
                  </button>
                  {cur.result_id && (
                    <Btn small onClick={() => ctx.openResult(cur.result_id)}>Open this result →</Btn>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <div className="text-xs font-bold text-grayscale-1200">Problem addressed</div>
                  <p className="text-sm text-grayscale-1300 break-words">{cur.problem || '—'}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="text-xs font-bold text-grayscale-1200">Example applications ({cur.applications.length})</div>
                  {cur.applications.length ? (
                    <ul className="list-disc pl-5 text-sm text-grayscale-1300 flex flex-col gap-0.5">
                      {cur.applications.map((a, i) => <li key={i} className="break-words">{a}</li>)}
                    </ul>
                  ) : <div className="text-sm text-grayscale-1100">None.</div>}
                </div>
                <div className="flex flex-col gap-1">
                  <div className="text-xs font-bold text-grayscale-1200">Value levers → technical tags ({cur.levers.length})</div>
                  <LeverChips levers={cur.levers} />
                </div>
                {cur.reasoning && (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-xs font-bold text-grayscale-1200">Reasoning</summary>
                    <p className="mt-1 text-grayscale-1300 whitespace-pre-wrap break-words">{cur.reasoning}</p>
                  </details>
                )}
              </>
            ) : (
              <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200 flex flex-col gap-1.5">
                <span>
                  <span className="font-bold">Pending</span> — no usable Horizon result under any prompt in the source
                  priority, so the public page shows placeholders for Problem &amp; applications and Value levers.
                  {results.length > 0 && ` It has ${results.length} result${results.length === 1 ? '' : 's'}, ${usable} usable (the rest errored or came back without a horizon, or belong to prompts not in the priority).`}
                </span>
                {tech.has_summary === false && (
                  <span className="font-medium">
                    It has no summary, so the batch runner will not send it — add a summary first.
                  </span>
                )}
                <span>
                  <Btn small onClick={() => ctx.goTab('Pending')}>Pending technologies &amp; how to run them →</Btn>
                </span>
              </div>
            )}
          </Section>

          <Section title={`All results (${results.length})`}>
            <p className="text-xs text-grayscale-1100 -mt-1">
              Every result this technology got, from every prompt and run, newest first. Click one to expand it.
            </p>
            {results.length ? (
              <div className="flex flex-col gap-2">
                {results.map((r: any, i: number) => (
                  <ResultCard key={str(r.id) || i} r={r} ctx={ctx}
                    isCurrent={r.is_current === true || (!!cur && !!cur.result_id && str(r.id) === cur.result_id)} />
                ))}
              </div>
            ) : <div className="text-sm text-grayscale-1100">No Horizon results for this technology yet.</div>}
          </Section>
        </>
      )}
    </Drawer>
  );
};

export default TechDrawer;
